import React, { useState, useEffect } from 'react';

const SdrCalendarView = ({ records, selectedDate, onDateSelect, onRecordSelect, onEditRecord, onDeleteRecord }) => {
  const [currentDate, setCurrentDate] = useState(selectedDate);
  const [calendarData, setCalendarData] = useState({});

  const monthNames = [
    '一月', '二月', '三月', '四月', '五月', '六月',
    '七月', '八月', '九月', '十月', '十一月', '十二月'
  ];

  const dayNames = ['日', '一', '二', '三', '四', '五', '六'];

  const statusColors = {
    'draft': { bg: '#e5e7eb', text: '#374151', label: '已儲存' },
    'pending': { bg: '#fef3c7', text: '#92400e', label: '待批准' },
    'approved': { bg: '#d1fae5', text: '#065f46', label: '已批准' }
  };

  const leaveTypeIcons = {
    '進修培訓': '📚',
    '公幹': '✈️',
    '事假': '🏠',
    '病假': '🏥',
    '會議': '👥'
  };

  const processCalendarData = React.useCallback(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const data = {};

    // Group records by date
    records.forEach(record => {
      if (record.start_date) {
        const recordDate = new Date(record.start_date);
        if (recordDate.getFullYear() === year && recordDate.getMonth() === month) {
          const dateStr = record.start_date;
          if (!data[dateStr]) {
            data[dateStr] = [];
          }
          data[dateStr].push(record);
        }
      }
    });

    setCalendarData(data);
  }, [records, currentDate]);

  useEffect(() => {
    processCalendarData();
  }, [processCalendarData]);


  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const handleDateClick = (date) => {
    if (date) {
      onDateSelect(date);
    }
  };

  const handleRecordClick = (record, event) => {
    event.stopPropagation();
    onRecordSelect(record);
  };

  const getDateStatus = (date) => {
    if (!date) return null;
    
    const dateStr = date.toISOString().split('T')[0];
    const today = new Date().toISOString().split('T')[0];
    
    if (dateStr === today) return 'today';
    
    const dayData = calendarData[dateStr];
    if (dayData && dayData.length > 0) {
      return 'has-records';
    }
    
    return 'normal';
  };

  const getDateRecords = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    return calendarData[dateStr] || [];
  };

  const getStatusColor = (status) => {
    return statusColors[status] || statusColors['draft'];
  };

  const getLeaveTypeIcon = (leaveType) => {
    return leaveTypeIcons[leaveType] || '📋';
  };

  const getMonthlyStats = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const monthStr = `${year}-${(month + 1).toString().padStart(2, '0')}`;
    
    const monthRecords = records.filter(record => 
      record.start_date && record.start_date.startsWith(monthStr)
    );

    const totalRecords = monthRecords.length;
    const totalAffectedStudents = monthRecords.reduce((sum, record) => {
      if (record.affected_lessons && Array.isArray(record.affected_lessons)) {
        return sum + record.affected_lessons.reduce((lessonSum, lesson) => {
          return lessonSum + (lesson.student_count || 0);
        }, 0);
      }
      return sum;
    }, 0);

    return { totalRecords, totalAffectedStudents };
  };

  const days = getDaysInMonth(currentDate);
  const monthlyStats = getMonthlyStats();

  return (
    <div className="sdr-calendar-view">
      <div className="calendar-header">
        <div className="calendar-navigation">
          <button 
            className="nav-btn"
            onClick={() => navigateMonth(-1)}
          >
            ◀
          </button>
          <h3>{currentDate.getFullYear()}年 {monthNames[currentDate.getMonth()]}</h3>
          <button 
            className="nav-btn"
            onClick={() => navigateMonth(1)}
          >
            ▶
          </button>
        </div>

        <div className="monthly-stats">
          <div className="stat-item">
            <span className="stat-number">{monthlyStats.totalRecords}</span>
            <span className="stat-label">當月記錄總數</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{monthlyStats.totalAffectedStudents}</span>
            <span className="stat-label">影響學生人數</span>
          </div>
        </div>
      </div>

      <div className="calendar-grid">
        <div className="calendar-header-row">
          {dayNames.map(day => (
            <div key={day} className="calendar-day-header">
              {day}
            </div>
          ))}
        </div>
        
        <div className="calendar-body">
          {days.map((date, index) => {
            if (!date) {
              return <div key={index} className="calendar-day empty"></div>;
            }

            const status = getDateStatus(date);
            const dayRecords = getDateRecords(date);
            const isSelected = selectedDate && date.toISOString().split('T')[0] === selectedDate.toISOString().split('T')[0];

            return (
              <div 
                key={index} 
                className={`calendar-day ${status} ${isSelected ? 'selected' : ''}`}
                onClick={() => handleDateClick(date)}
              >
                <div className="day-number">{date.getDate()}</div>
                
                {dayRecords.length > 0 && (
                  <div className="day-records">
                    {dayRecords.map((record, recordIndex) => {
                      const statusColor = getStatusColor(record.status);
                      return (
                        <div 
                          key={recordIndex} 
                          className="record-card"
                          style={{ 
                            backgroundColor: statusColor.bg,
                            color: statusColor.text,
                            borderColor: statusColor.text
                          }}
                          onClick={(e) => handleRecordClick(record, e)}
                          title={`${record.teacher_name} - ${record.leave_type} - ${statusColor.label}`}
                        >
                          <div className="record-header">
                            <span className="record-icon">
                              {getLeaveTypeIcon(record.leave_type)}
                            </span>
                            <span className="record-status">
                              {statusColor.label}
                            </span>
                          </div>
                          <div className="record-content">
                            <div className="teacher-name">{record.teacher_name}</div>
                            <div className="leave-type">{record.leave_type}</div>
                            <div className="department">{record.department}</div>
                          </div>
                          <div className="record-actions">
                            <button 
                              className="action-btn edit-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditRecord(record);
                              }}
                              title="編輯記錄"
                            >
                              ✏️
                            </button>
                            <button 
                              className="action-btn delete-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm('確定要刪除這個SDR記錄嗎？')) {
                                  onDeleteRecord(record.id);
                                }
                              }}
                              title="刪除記錄"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="calendar-legend">
        <h4>記錄狀態圖例</h4>
        <div className="legend-items">
          {Object.entries(statusColors).map(([status, color]) => (
            <div key={status} className="legend-item">
              <span 
                className="legend-color" 
                style={{ backgroundColor: color.bg, color: color.text }}
              ></span>
              <span className="legend-label">{color.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="calendar-instructions">
        <div className="instruction-item">
          <span className="instruction-icon">📅</span>
          <span className="instruction-text">點擊空白日期建立新的SDR記錄</span>
        </div>
        <div className="instruction-item">
          <span className="instruction-icon">📋</span>
          <span className="instruction-text">點擊彩色記錄查看詳細資訊和編輯</span>
        </div>
      </div>
    </div>
  );
};

export default SdrCalendarView;
